import * as Lodash from 'lodash'
import * as LodashFP from 'lodash/fp'
import moment from 'moment'

import {
  normalize,
  build,
  buildAll,
  decode,
  buildFirst,
} from '../../utils/json-api'
import { AdapterContext, labeledFromApi } from '../../interfaces/base'
import { Project, UnsavedProjectType } from '../../interfaces/entities/project'
import { Campaign } from '../../interfaces/entities/campaign'
import { Client } from '../../interfaces/entities/client'
import {
  Creative,
  CreativeAction,
  creativeHasTags,
} from '../../interfaces/entities/creative'
import { MultifileCreative } from '../../mocks/blueprints/creatives'
import { IStringAnyMap, IStringTMap } from '../../types/map'
import {
  FormBlueprint,
  FormFieldBlueprint,
  IFormValidation,
  emptyFormGuidelines,
  FormGuidelinesType,
  GuidelineCondition,
  Guideline,
  emptyFieldGuidelines,
} from '../../interfaces/components/dynamic-form'
import { PaginationResult } from '../../interfaces/services/projects'
import { Formatset } from '../../interfaces/entities/formatset'
import { Comment } from '../../interfaces/entities/comment'
import { FileObject } from '../../interfaces/services/files'
import {
  AdvertiserUser,
  User,
  UserEmailData,
} from '../../interfaces/entities/user'
import { getValidationMessages } from '../../utils/error'
import { State as IState } from '../../model'
import DI from '../../app/di'
import { translateWithUserLocale } from '../../utils/i18n'
import {
  removeLastElementFromPathString,
  removeFirstElementFromPathString,
} from '../../utils/string'
import { Platform } from '../../interfaces/entities/platform'

function getFieldType(field: any) {
  switch (LodashFP.getOr(null, 'type', field)) {
    case 'set':
      return { type: 'select' }
    case 'enum':
      return { type: 'select' }
    case 'string':
      return { type: 'text' }
    case 'object':
      return { type: 'fieldset' }
    case 'array':
      return { type: 'list' }
    case 'file':
      return { type: 'file' }
    case 'boolean':
      return { type: 'boolean' }
    case 'integer':
      return { type: 'integer' }
    default:
      return { type: null }
  }
}

function getFieldBase(field: any) {
  return {
    mandatory: !!(field.required || false),
    step: LodashFP.getOr(0, ['step'], field),
    name: LodashFP.getOr('', ['name'], field),
    label: LodashFP.getOr('', ['label'], field),
    description: LodashFP.getOr('', ['render', 'description'], field),
    hidden: LodashFP.getOr(false, ['render', 'hidden'], field),
    collapsable: LodashFP.getOr(false, ['render', 'collapsed'], field),
  }
}

function getSelectType(renderComponent: string | null) {
  switch (renderComponent) {
    case 'button':
      return 'grid'
    case 'checkbox':
      return 'checkboxes'
    case 'elevator':
      return 'arrows'
    case 'radiobutton':
      return 'radiobutton'
    default:
      return 'dropdown'
  }
}

function getFieldMeta(field: any) {
  switch (field.type) {
    case 'set':
      const selectType = getSelectType(
        LodashFP.getOr(null, ['render', 'component'], field),
      )
      return {
        meta: {
          as: selectType,
          multiple: true,
          options: LodashFP.sortBy(
            (x) => x.label,
            field.of.values.map((option: any) => {
              return {
                label: LodashFP.getOr('', ['label'], option),
                value: option.value != null ? option.value : null,
              }
            }),
          ),
          default: field.of.values
            .filter((o: any) => o.default)
            .map((o: any) => o.value),
        },
      }
    case 'enum':
      const def = field.values.find((o: any) => o.default)
      const meta = {
        as: getSelectType(LodashFP.getOr(null, ['render', 'component'], field)),
        multiple: false,
        options: LodashFP.sortBy(
          (x) => x.label,
          field.values.map((option: any) => {
            return {
              label: LodashFP.getOr('', ['label'], option),
              value: option.value != null ? option.value : null,
            }
          }),
        ),
      } as any

      if (def) {
        meta.default = def.value
      }

      return {
        meta,
      }
    case 'string':
      const stringMeta: { maximumCharacters?: number; default?: string } = {}
      if ('max_characters' in field) {
        stringMeta.maximumCharacters = field.max_characters
      }
      if ('default' in field) {
        stringMeta.default = field.default
      }
      return Object.keys(stringMeta).length > 0
        ? {
            meta: stringMeta,
          }
        : {}
    case 'object':
      return {
        fields: field.fields
          ? field.fields.map((f: any) => getFieldBlueprint(f))
          : [],
      }
    case 'array':
      return {
        meta: {
          of: getFieldBlueprint(field.of),
          minimumElements: field.min || undefined,
        },
      }
    case 'file':
      const fileType = LodashFP.getOr(undefined, ['render', 'file_type'], field)
      return {
        meta: {
          maximumSize: Infinity,
          multiple: false,
          allowedTypes: [],
          fileType: ['image', 'video', 'subtitle', undefined].includes(fileType)
            ? fileType
            : undefined,
        },
      }
    case 'boolean':
      return {
        meta: {
          default: field.default || false,
        },
      }
    case 'integer':
      return {
        meta: {
          default: field.default == null ? null : field.default,
          max: field.max,
          min: field.min,
        },
      }
    default:
      return {}
  }
}

function getFieldBlueprint(field: any): FormFieldBlueprint {
  return {
    ...getFieldType(field),
    ...getFieldBase(field),
    ...getFieldMeta(field),
  } as FormFieldBlueprint // TODO: Remove 'as' typecasting
}

function creativeActions(
  creative: Creative,
  meta: { user: User; project?: Project; formatset?: Formatset },
): CreativeAction[] {
  const { user, project, formatset } = meta
  const actions = [...creative.possibleActions]

  if (project !== undefined) {
    let fset =
      formatset ||
      project.formatsets.find((fs) => fs.id === creative.formatsetId)
    if (project.status.name !== 'archived' && fset && !fset.invalid) {
      actions.push({
        name: 'duplicate',
        label: translateWithUserLocale('duplicate'),
      })
    }
  }

  if (
    creative.status.name === 'sent_to_agency' &&
    creativeHasTags(creative, ['published', 'wip']) &&
    !user.groups.includes('client')
  ) {
    actions.push({
      name: 'broadcast',
      label: translateWithUserLocale('broadcast'),
    })
  }

  return actions
}

const fromBuilt = {
  project: (data: IStringAnyMap, meta: IStringAnyMap = {}) => {
    const baseProject: Project = {
      id: data.id,
      name: data.name,
      contacts: (data.client_contacts || []).map((c: any) =>
        fromBuilt.contact(c),
      ),
      pmContacts: (data.memoria_contacts || []).map((c: any) =>
        fromBuilt.contact(c),
      ),
      campaigns: (data.memoria_campaigns || []).map((c: any) => {
        return fromBuilt.campaign(c)
      }),
      client: {
        id: data.client_id.toString(),
        name: data.client_name,
        contacts: [],
        pmContacts: [],
        advertiserUsers: [],
      },
      status: {
        name: data.state.value,
        title: data.state.label,
        description: data.state_description,
      },
      possibleActions: data.actions.map((action: any) => {
        return {
          name: action.value,
          title: action.label,
          description: action.label,
        }
      }),
      comment: data.comment != null ? fromBuilt.comment(data.comment) : null,
      deadline: moment(data.deadline),
      creativeStates: meta.creative_states
        ? Object.keys(meta.creative_states).map((state) => {
            return {
              name: state,
              label: meta.creative_states[state],
            }
          })
        : [],
      formatsets: [],
      isFavorite: data.is_favorite,
      message: meta.message && meta.message.length > 0 ? meta.message[0] : null,
    }

    const formatsets = (data.formatsets || []).map((fs: any) => {
      return fromBuilt.formatset(fs, baseProject)
    })

    return { ...baseProject, formatsets }
  },

  campaign: (data: IStringAnyMap) => {
    return {
      id: data.id,
      name: data.name,
      projectNumber: data.project_number || '',
    }
  },

  contact: (c: IStringAnyMap) => {
    return {
      id: c.id,
      name: `${c.first_name} ${c.last_name}`,
      email: c.email_address || undefined,
      phone: c.cellphone_number || c.phone_number || undefined,
      role: c.role || undefined,
      title: c.title || undefined,
      hidden: c.is_senior && c.is_senior === '1' ? true : false,
    }
  },
  platform: (c: IStringAnyMap) => {
    return {
      value: c.id,
      label: c.label,
    }
  },

  formatset: (data: IStringAnyMap, project?: Project) => {
    const baseFormatset: Formatset = {
      id: data.id,
      name: data.name,
      type: data.formatset.type,
      data: data.formatset,
      displayOrder: data.display_order,
      creativeFormats:
        data.creative_formats.map((f: { value: string; label: string }) => ({
          name: f.value,
          label: f.label,
        })) || [],
      comment: null, // TODO: Where can we get this??
      creatives: [],
      invalid: data.invalid || false,
      blueprint: data.blueprint
        ? Lodash.isArray(data.blueprint) && data.blueprint.length === 0
          ? undefined
          : fromBuilt.blueprint(data.blueprint, { name: 'formatset' })
        : undefined,
    }

    const creatives = (data.creatives || []).map((c: any) => {
      const creativeWithActions = {
        ...c,
        possible_actions: c.meta.possible_transitions,
        state_history: c.meta.state_history,
        migration_result: c.meta.migration_result,
      }
      return fromBuilt.creative(creativeWithActions, {
        formatset: baseFormatset,
        project,
      })
    })

    return { ...baseFormatset, creatives }
  },
  formatsetImport: (data: IStringAnyMap) => {
    return {
      invalidFormatsets:
        data.meta.key === 'invalid_formatsets' ? data.meta.value : null,
    }
  },
  client: (data: IStringAnyMap) => {
    return {
      id: data.id,
      name: data.name,
      shortName: data.short_name || data.name,
      contacts: (data.client_contacts || []).map((c: any) =>
        fromBuilt.contact(c),
      ),
      pmContacts: (data.pm_contacts || []).map((c: any) =>
        fromBuilt.contact(c),
      ),
      advertiserUsers: (data.advertiser_users || []).map((au: any) =>
        fromBuilt.advertiserUser(au),
      ),
    }
  },

  advertiserUser: (au: IStringAnyMap) => {
    return {
      id: au.id,
      advertiserId: au.advertiser_id,
      username: au.username,
      email: au.email,
      firstName: au.first_name,
      lastName: au.last_name,
      restrictedAccess: au.restricted_access,
    } as AdvertiserUser
  },
  creative: (
    data: IStringAnyMap,
    meta: { formatset?: Formatset; project?: Project },
  ) => {
    const { formatset, project } = meta
    const storeState: () => IState = () => DI.get('store').getState()
    const user = storeState().user
    const creative = {
      id: data.id,
      name: data.name,
      status: labeledFromApi(data.state),
      type: { name: data.creative_type.value, label: data.creative_type.label },
      displayOrder: data.display_order,
      possibleActions: data.possible_actions.map(
        (action: { value: string; label: string }) => labeledFromApi(action),
      ),
      tags: data.tags ? data.tags : [],
      comment: data.comment != null ? fromBuilt.comment(data.comment) : null,
      reopens: LodashFP.getOr([], 'state_history.reopens', data).map(
        (reopen: any) => {
          return {
            type: {
              name: 'reopen',
              label: 'Reopened after send to agency',
            },
            time: moment(reopen.date, 'DD-MM-YYYY HH:mm:ss'),
            user: reopen.user,
          }
        },
      ),
      sent: LodashFP.getOr([], 'state_history.sent', data).map((sent: any) => {
        return {
          type: {
            name: 'sent',
            label: 'Sent to agency',
          },
          time: moment(sent.date, 'DD-MM-YYYY HH:mm:ss'),
          user: sent.user,
        }
      }),
      outdated: LodashFP.getOr([], 'state_history.outdated', data).map(
        (outdated: any) => {
          return {
            type: {
              name: 'outdated',
              label: 'Outdated creative',
            },
            time: moment(outdated.date, 'DD-MM-YYYY HH:mm:ss'),
            user: outdated.user,
          }
        },
      ),
      blueprint: MultifileCreative,
      data: data.creative,
      formatsetId: data.formatset_id,
      guidelines: emptyFormGuidelines(),
      blueprintMeta: {
        version: data.version,
        outdated: data.invalid || false,
      },
      creativeMigration: data.migration_result,
    }
    const actions = creativeActions(creative, { user, project, formatset })
    return {
      ...creative,
      possibleActions: actions,
    }
  },
  comment: (data: IStringAnyMap) => {
    if (data == null) {
      return {
        content: '',
        author: '',
      }
    }

    return {
      content: data.body || '',
      author: data.author || '',
    }
  },

  user: (data: IStringAnyMap) => {
    if (data === null) {
      return {
        fullName: '',
        email: '',
        groups: [],
        restrictedAccess: true,
      }
    }
    return {
      fullName: data.full_name,
      email: data.email,
      groups: data.groups,
      restrictedAccess: data.restricted_access,
    }
  },
  userEmailData: (data: IStringAnyMap) => {
    if (data === null) {
      return {
        agency: {
          name: '',
          logo: '',
          url: '',
          address: {
            street: '',
            city: '',
            zip: '',
          },
          financeData: {
            companyId: '',
            taxId: '',
            managingDirectors: '',
          },
        },
        agent: {
          title: '',
          firstname: '',
          lastname: '',
          email: '',
          phone: '',
        },
        hostname: '',
      }
    }
    return {
      agency: {
        name: data.agency.name,
        logo: data.agency.logo,
        url: data.agency.url,
        address: {
          street: data.agency.address.street,
          city: data.agency.address.city,
          zip: data.agency.address.zip,
        },
        financeData: {
          companyId: data.agency.finance_data.company_id,
          taxId: data.agency.finance_data.tax_id,
          managingDirectors: data.agency.finance_data.managing_directors,
        },
      },
      agent: {
        title: data.agent.title,
        firstname: data.agent.firstname,
        lastname: data.agent.lastname,
        email: data.agent.email,
        phone: data.agent.phone,
      },
      hostname: data.hostname,
    }
  },

  blueprint: (data: IStringAnyMap, options: IStringAnyMap = {}) => {
    const { name = 'form-blueprint' } = options
    const fields = data.fields
      ? data.fields.map((field: any) => {
          return getFieldBlueprint(field)
        })
      : []

    const blueprint: FormBlueprint = {
      name,
      fields: fields,
    }

    return blueprint
  },
}

const adapters: {
  unsavedProject: AdapterContext<UnsavedProjectType>
  project: AdapterContext<Project>
  projects: AdapterContext<Project[]>
  projectAccess: AdapterContext<Project[]>
  campaigns: AdapterContext<Campaign[]>
  client: AdapterContext<Client>
  clients: AdapterContext<Client[]>
  creative: AdapterContext<Creative>
  guidelines: AdapterContext<FormGuidelinesType>
  creatives: AdapterContext<Creative[]>
  formatset: AdapterContext<Formatset>
  formatsets: AdapterContext<Formatset[]>
  blueprint: AdapterContext<FormBlueprint>
  pagination: AdapterContext<PaginationResult>
  comment: AdapterContext<Comment>
  file: AdapterContext<FileObject>
  formValidation: AdapterContext<IFormValidation>
  user: AdapterContext<User>
  userEmailData: AdapterContext<UserEmailData>
  platform: AdapterContext<Platform>
  platforms: AdapterContext<Platform[]>
} = {
  unsavedProject: {
    default: {
      decode: (data) => {
        console.log(
          'decoding unsaved project (we should never end up here though) from: ',
          data,
        )
        return {
          id: '1',
          name: 'Unsaved Project',
          contacts: [],
          pmContacts: [],
          campaigns: [],
          client: {
            id: '1',
            name: 'Unknown Client',
            contacts: [],
            pmContacts: [],
            advertiserUsers: [],
          },
          status: {
            name: 'no_status',
            title: 'no_status',
            description: 'no_status',
          },
          possibleActions: [],
          formatsets: [],
          comment: null,
          deadline: moment(),
        }
      },
      encode: (project) => {
        return {
          name: project.name,
          client_id: project.client ? project.client.id : null,
          deadline: project.deadline
            ? project.deadline.format('YYYY-MM-DD hh:mm')
            : null,
          client_contacts: project.contacts.map((c) => c.id),
          memoria_contacts: project.pmContacts.map((c) => c.id),
          memoria_campaigns: project.campaigns.map((c) => c.id),
        }
      },
    },
  },

  project: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const projectId = options.id || Object.keys(normalized.project)[0]
        const built = build(normalized, 'project', projectId, {
          ignoreLinks: true,
        })
        return fromBuilt.project(built, response.meta)
      },

      encode: (project) => {
        return {
          name: project.name,
          client_id: project.client.id,
          deadline: project.deadline.format('YYYY-MM-DD hh:mm'),
          client_contacts: project.contacts.map((c) => c.id),
          memoria_contacts: project.pmContacts.map((c) => c.id),
          memoria_campaigns: project.campaigns.map((c) => c.id),
        }
      },
    },

    withNextAction: {
      decode: (response, options) => {
        const fromDefault = decode(adapters.project, response)
        const projectAction = LodashFP.getOr(
          {
            name: '-',
            label: '-',
            description: '-',
          },
          ['meta', 'project_action'],
          response,
        )
        const stateDescription = LodashFP.getOr(
          '',
          ['meta', 'state_description'],
          response,
        )

        const nextAction = {
          name: projectAction.name,
          title: projectAction.label,
          description: projectAction.description,
        }

        const project = {
          ...fromDefault,
          status: {
            ...fromDefault.status,
            description: stateDescription,
          },
        }

        return {
          nextAction,
          ...project,
        }
      },
      encode: (project) => {
        return {}
      },
    },
  },

  projectAccess: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const built = buildAll(normalized, 'project', response)
        return built.map((data) => {
          return fromBuilt.project(data, {})
        })
      },
      encode: () => {
        return {}
      },
    },
  },

  projects: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const projectEntities: IStringAnyMap = {}
        if (response && response.data) {
          const projects = response.data
            .map((project: any) => project.id)
            .map((id: string) => normalized.project[id])

          for (const project of projects) {
            projectEntities[project.id] = project
          }
        }

        const built = buildAll(
          {
            ...normalized,
            project: projectEntities,
          },
          'project',
          response,
        )

        return built.map((data) => {
          return fromBuilt.project(data, response.meta)
        })
      },
      encode: (projects) => {
        return []
      },
    },
  },

  client: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const clientId = options.id || Object.keys(normalized.project)[0]
        const built = build(normalized, 'memoria_client', clientId, {
          ignoreLinks: true,
        })
        return fromBuilt.client(built)
      },

      encode: (client) => {
        return {}
      },
    },
  },

  clients: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const built = buildAll(normalized, 'memoria_client', response)

        const adapted = built.map((data) => {
          return fromBuilt.client(data)
        })

        return adapted
      },
      encode: (clients) => {
        return []
      },
    },
  },

  campaigns: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const built = buildAll(normalized, 'memoria_campaign', response)
        const adapted = built.map((data) => {
          return fromBuilt.campaign(data)
        })

        return adapted
      },
      encode: (campaigns) => {
        return []
      },
    },
  },

  guidelines: {
    default: {
      decode: (response, options) => {
        /*
                          !! HERE BE DRAGONS !!
                    Avert your eyes, sweet summer child
                                                                    ___
                                                        .~))>>
                                                        .~)>>
                                                      .~))))>>>
                                                    .~))>>             ___
                                                  .~))>>)))>>      .-~))>>
                                                .~)))))>>       .-~))>>)>
                                              .~)))>>))))>>  .-~)>>)>
                          )                 .~))>>))))>>  .-~)))))>>)>
                      ( )@@*)             //)>))))))  .-~))))>>)>
                    ).@(@@               //))>>))) .-~))>>)))))>>)>
                  (( @.@).              //))))) .-~)>>)))))>>)>
                ))  )@@*.@@ )          //)>))) //))))))>>))))>>)>
              ((  ((@@@.@@             |/))))) //)))))>>)))>>)>
            )) @@*. )@@ )   (\_(\-\b  |))>)) //)))>>)))))))>>)>
          (( @@@(.@(@ .    _/`-`  ~|b |>))) //)>>)))))))>>)>
            )* @@@ )@*     (@) (@)  /\b|))) //))))))>>))))>>
          (( @. )@( @ .   _/       /  \b)) //))>>)))))>>>_._
          )@@ (@@*)@@.  (6,   6) / ^  \b)//))))))>>)))>>   ~~-.
        ( @jgs@@. @@@.*@_ ~^~^~, /\  ^  \b/)>>))))>>      _.     `,
        ((@@ @@@*.(@@ .   \^^^/' (  ^   \b)))>>        .'         `,
          ((@@).*@@ )@ )    `-'   ((   ^  ~)_          /             `,
            (@@. (@@ ).           (((   ^    `\        |               `.
              (*.@*              / ((((        \        \      .         `.
                                /   (((((  \    \    _.-~\     Y,         ;
                              /   / (((((( \    \.-~   _.`" _.-~`,       ;
                              /   /   `(((((()    )    (((((~      `,     ;
                            _/  _/      `"""/   /'                  ;     ;
                        _.-~_.-~           /  /'                _.-~   _.'
                      ((((~~              / /'              _.-~ __.--~
                                        ((((          __.-~ _.-~
                                                    .'   .~~
                                                    :    ,'
                                                    ~~~~~
        */

        const groupFieldsForGuideline = (
          data: IStringAnyMap,
        ): IStringTMap<Guideline> => {
          const keys = Object.keys(data).map((key) => {
            return [key, data[key]]
          })
          const grouped = LodashFP.groupBy((kv) => {
            return removeLastElementFromPathString(kv[0])
          }, keys)

          const final: IStringAnyMap = {}

          Object.keys(grouped).forEach((key) => {
            final[key] = {}
            grouped[key].forEach((kv) => {
              const [key2, value] = kv
              const path = key2.split('.')
              const last = path[path.length - 1]
              final[key][last] = value
            })
          })

          return final
        }

        const unconditionalRequired = groupFieldsForGuideline(
          response.required.unconditional,
        )
        const unconditionalRecommended = groupFieldsForGuideline(
          response.recommended.unconditional,
        )

        const guidelines: IStringAnyMap = {}

        Object.keys(unconditionalRecommended).forEach((field) => {
          if (!guidelines.hasOwnProperty(field)) {
            guidelines[field] = emptyFieldGuidelines()
          }

          const guideline = unconditionalRecommended[field]
          guidelines[field].recommendations.unconditional = {
            ...guidelines[field].unconditional,
            ...guideline,
          }
        })

        Object.keys(unconditionalRequired).forEach((field) => {
          if (!guidelines.hasOwnProperty(field)) {
            guidelines[field] = emptyFieldGuidelines()
          }

          const guideline = unconditionalRequired[field]
          guidelines[field].requirements.unconditional = {
            ...guidelines[field].unconditional,
            ...guideline,
          }
        })

        const conditionMapper = (cond: any) => {
          const condition = {
            type: cond.type || 'empty',
            field: cond.field,
            complement: cond.complement || false,
          }

          if (condition.type === 'in') {
            return { ...condition, values: cond.values || [] }
          }

          if (condition.type === 'is') {
            return { ...condition, type: 'in', values: [cond.values] }
          }

          if (condition.type === 'between') {
            return { ...condition, min: cond.min, max: cond.max }
          }

          if (condition.type === 'min') {
            return { ...condition, min: cond.min }
          }

          if (condition.type === 'max') {
            return { ...condition, max: cond.max }
          }

          return condition as GuidelineCondition
        }

        response.recommended.conditional.forEach((x: any) => {
          const conditions = x.conditions.map(
            conditionMapper,
          ) as GuidelineCondition[]
          const groupedFields = groupFieldsForGuideline(
            LodashFP.omit('conditions', x),
          )

          Object.keys(groupedFields).forEach((field) => {
            const guideline: Guideline = groupedFields[field]
            if (!guidelines.hasOwnProperty(field)) {
              guidelines[field] = emptyFieldGuidelines()
            }
            guidelines[field].recommendations.conditional.push({
              conditions,
              guidelines: guideline,
            })
          })
        })

        response.required.conditional.forEach((x: any) => {
          const conditions = x.conditions.map(
            conditionMapper,
          ) as GuidelineCondition[]
          const groupedFields = groupFieldsForGuideline(
            LodashFP.omit('conditions', x),
          )

          Object.keys(groupedFields).forEach((field) => {
            const guideline: Guideline = groupedFields[field]
            if (!guidelines.hasOwnProperty(field)) {
              guidelines[field] = emptyFieldGuidelines()
            }
            guidelines[field].requirements.conditional.push({
              conditions,
              guidelines: guideline,
            })
          })
        })

        return Lodash.mapKeys(guidelines, (value: any, key: string) => {
          return removeFirstElementFromPathString(key)
        }) as FormGuidelinesType
      },
      encode: (_) => {
        return emptyFormGuidelines()
      },
    },
  },

  creative: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const creativeId = options.id || Object.keys(normalized.creative)[0]
        const built = build(normalized, 'creative', creativeId, {
          ignoreLinks: true,
        })
        const formatset =
          (buildFirst(normalized, 'formatset') as Formatset | null) || undefined

        const data = {
          ...built,
          formatset,
          possible_actions: response.meta.possible_transitions,
          state_history: response.meta.state_history,
          migration_result: response.meta.migration_result,
        }

        return fromBuilt.creative(data, { formatset })
      },
      encode: (creative) => {
        return {}
      },
    },

    withProject: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const creativeId = options.id || Object.keys(normalized.creative)[0]

        const built = build(normalized, 'creative', creativeId, {
          ignoreLinks: true,
        })
        const data = {
          ...built,
          possible_actions: response.meta.possible_transitions,
          state_history: response.meta.state_history,
          migration_result: response.meta.migration_result,
        }

        const project = fromBuilt.project(
          {
            ...data.formatset.project,
            formatsets: [data.formatset],
          },
          response.meta,
        )

        const creative = fromBuilt.creative(data, { project })

        return {
          project,
          ...creative,
        }
      },
      encode: (creative) => {
        return {}
      },
    },
  },

  creatives: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const built = buildAll(normalized, 'creative', response)

        const data = built.map((creative) => {
          return {
            ...creative,
            possible_actions: response.meta.possible_transitions,
            state_history: response.meta.state_history,
            migration_result: response.meta.migration_result,
          }
        })

        return data.map((creative) => {
          return {
            id: creative.id,
            formatsetId: creative.formatset_id,
            name: creative.creative.name,
            status: creative.state,
            displayOrder: creative.display_order,
            type: creative.creative.creative_type,
            possibleActions: creative.possible_actions.map(
              (action: { value: string; label: string }) =>
                labeledFromApi(action),
            ),
            tags: creative.tags ? creative.tags : [],
            comment: null,
            reopens: LodashFP.getOr(
              [],
              ['state_history', 'reopens'],
              creative,
            ).map((reopen: any) => {
              return {
                type: {
                  name: 'reopen',
                  label: 'Reopened after send to agency',
                },
                time: moment(reopen.date, 'DD-MM-YYYY HH:mm:ss'),
                user: reopen.user,
              }
            }),
            sent: LodashFP.getOr([], ['state_history', 'sent'], creative).map(
              (sent: any) => {
                return {
                  type: {
                    name: 'sent',
                    label: 'Sent to agency',
                  },
                  time: moment(sent.date, 'DD-MM-YYYY HH:mm:ss'),
                  user: sent.user,
                }
              },
            ),
            outdated: LodashFP.getOr(
              [],
              ['state_history', 'outdated'],
              creative,
            ).map((outdated: any) => {
              return {
                type: {
                  name: 'outdated',
                  label: 'Outdated creative',
                },
                time: moment(outdated.date, 'DD-MM-YYYY HH:mm:ss'),
                user: outdated.user,
              }
            }),
            blueprint: MultifileCreative,
            blueprintMeta: {
              version: creative.version,
              outdated: creative.invalid || false,
            },
            data: {},
            guidelines: emptyFormGuidelines(),
            creativeMigration: creative.migration_result,
          }
        })
      },
      encode: (creative) => {
        return {}
      },
    },
  },

  formatset: {
    default: {
      decode: (response, _options) => {
        if (response.data && response.data.relationships) {
          delete response.data.relationships
        }
        const normalized = normalize(response)
        const built = build(normalized, 'formatset')
        const formatset = built[0]

        return fromBuilt.formatset(formatset)
      },
      encode: (formatset) => {
        return {}
      },
    },
  },

  formatsets: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)
        const formatsetEntities: IStringAnyMap = {}
        if (response && response.data) {
          const formatsets = response.data
            .map((formatset: any) => formatset.id)
            .map((id: string) => normalized.formatset[id])

          for (const formatset of formatsets) {
            formatsetEntities[formatset.id] = formatset
          }
        }

        const built = buildAll(
          {
            ...normalized,
            formatset: formatsetEntities,
          },
          'formatset',
          response,
        )

        return built.map((data) => {
          return fromBuilt.formatset(data, response.meta)
        })
      },
      encode: (formatset) => {
        return []
      },
    },
  },

  blueprint: {
    default: {
      decode: (response, options = {}) => {
        if (options.built) {
          return fromBuilt.blueprint(response, options)
        }

        const normalized = normalize(response)
        const built = buildFirst(normalized, Object.keys(normalized)[0])

        return fromBuilt.blueprint(built, options)
      },

      encode: (client) => {
        return {}
      },
    },
  },

  pagination: {
    default: {
      decode: (response) => {
        return {
          page: LodashFP.getOr('', ['meta', 'current_page'], response),
          totalRows: LodashFP.getOr('', ['meta', 'total_rows'], response),
          previous: null,
          next: null,
        }
      },
      encode: (pagination) => {
        return {}
      },
    },
  },

  comment: {
    default: {
      decode: (response, options) => {
        const normalized = normalize(response)

        const commentId = options.id || Object.keys(normalized.comment)[0]
        const built = build(normalized, 'comment', commentId, {
          ignoreLinks: true,
        })

        return fromBuilt.comment(built)
      },
      encode: (pagination) => {
        return {}
      },
    },
  },

  file: {
    default: {
      decode: (response) => {
        const normalized = normalize(response)
        const key = 'asset' in normalized ? 'asset' : 'asset_info'
        const built = build(normalized, key, response.data.id, {
          ignoreLinks: true,
        })

        return {
          id: built.id,
          url: response.data.links.self,
          meta: built,
        }
      },
      encode: (formatset) => {
        return {}
      },
    },
  },
  formValidation: {
    default: {
      decode: (response) => {
        return {
          validUntil: response.data.valid_before_step,
          fields: getValidationMessages(
            response.data.messages,
            response.blueprint,
          ),
        }
      },
      encode: (formatset) => {
        return {}
      },
    },
  },
  user: {
    default: {
      decode: (response) => {
        const normalized = normalize(response)
        const userId = Object.keys(normalized.user)[0]

        const built = build(normalized, 'user', userId, { ignoreLinks: true })

        return fromBuilt.user(built)
      },
      encode: (formatset) => {
        return {}
      },
    },
  },
  userEmailData: {
    default: {
      decode: (response) => {
        const normalized = normalize(response)
        const userId = Object.keys(normalized.memoria_client_data)[0]

        const built = build(normalized, 'memoria_client_data', userId, {
          ignoreLinks: true,
        })

        return fromBuilt.userEmailData(built)
      },
      encode: (formatset) => {
        return {}
      },
    },
  },
  platform: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const id = options.id
        const built = build(normalized, 'platform', id, { ignoreLinks: true })
        return fromBuilt.platform(built)
      },

      encode: (platform) => {
        return {}
      },
    },
  },

  platforms: {
    default: {
      decode: (response, options = {}) => {
        const normalized = normalize(response)
        const built = buildAll(normalized, 'platform', response)

        const adapted = built.map((data) => {
          return fromBuilt.platform(data)
        })

        return adapted
      },
      encode: (platforms) => {
        return []
      },
    },
  },
}

export default adapters
